import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'

// Components
import { AddContactModal } from '../../../_views/factoring/lender/DebtorDetails/views/DebtorDetails_Contacts/AddContactModal'
import { Button } from '../../../_components/Button'
import { FormField } from '../../../_components/Forms/FormField'
import { LoaderButton } from '../../LoaderButton'
import { S_NoteEditor, normalizeButtonWidth, splitButtonCss } from './VerificationNoteEditor.styles'
import { SplitButton } from '../../SplitButton'

// Services
import { getTenantVerificationTypes } from '../../../_services/fvadmin_service'
import { getContact, getContacts } from '../../../_services/debtor.service'

// Helpers
import { getContactsFromClientsByDebtor, history } from '../../../_helpers'
import { logError } from '../../../_helpers/errors'

// Constants
import { LENDER_ROUTES } from '../../../_constants/routes'

// Hooks
import { notificationActionHooks } from '../../../_actionHooks/notification.actionHooks'
import { useModal } from '../../../_reduxHooks/modal.hook'

function selectFormatter(param) {
  return {
    display: param.name,
    value: param.id,
  }
}

export const VerificationNoteEditor = ({
  businessName,
  callFromModal,
  className,
  creatingVerification,
  debtorId,
  flexType,
  label,
  onChange,
  previousModalProps,
  refresh,
  save,
  value,
}) => {
  const { closeModal, openModal, setPrevModal, switchModal } = useModal()
  const showNotification = notificationActionHooks.useShowNotification()

  const [availableTypes, setAvailableTypes] = useState([])
  const [contactVal, setContactVal] = useState(null)
  const [contacts, setContacts] = useState([])
  const [fileNames, setFileNames] = useState([])
  const [textVal, setTextVal] = useState('')
  const [typeVal, setTypeVal] = useState(null)
  const fileRef = useRef()

  const formattedContacts = useMemo(() => {
    const formattedContacts = contacts.map(selectFormatter)
    return formattedContacts
  }, [contacts])

  const noContacts = formattedContacts.length === 0

  const formattedAvailableTypes = useMemo(() => {
    return availableTypes.map(selectFormatter)
  }, [availableTypes])

  const handleContactModal = async (props) => {
    const { callFromModal, previousModalProps } = props
    const readOnly = get(props, 'readOnly', false)

    if (debtorId) {
      const modalOptions = {
        callFromModal,
        closeModal,
        debtor: { id: debtorId },
        previousModalProps,
        readOnly: readOnly,
        refresh: refresh,
        showNotification,
      }
      if (contactVal && readOnly) {
        const data = await getContact(debtorId, contactVal)

        modalOptions.clientId = contactVal
        modalOptions.data = data
      }

      if (callFromModal) {
        switchModal(AddContactModal, modalOptions)

        const { Modal, ...restModalProps } = previousModalProps
        setPrevModal(Modal, {
          Modal: Modal,
          ...restModalProps,
        })
      } else {
        openModal(AddContactModal, modalOptions)
      }
    }
  }

  const handleFileChange = useCallback((e) => {
    let f = e.target.files
    let newNames = []
    for (let i = 0; i < f.length; i++) {
      newNames.push(f[i].name)
    }
    setFileNames(newNames)
  }, [])

  const resetValues = () => {
    setTextVal('')
    setFileNames([])
    setContactVal(() => '')
    setTypeVal(() => '')
  }

  const handleSave = (e) => {
    if (typeVal) {
      const files = fileNames.length > 0 ? fileRef.current.files : []
      save({
        contact: contactVal,
        files,
        text: textVal,
        type: typeVal,
      })
      resetValues()
    } else {
      showNotification({ id: 'error-create-note', type: 'error', message: 'Note type is required.' })
    }
  }

  useEffect(() => {
    getTenantVerificationTypes().then((t) => {
      setAvailableTypes(t)
    })
    getContacts(debtorId).then((debtorContactList) => {
      const contactsFromClientsBysDebtor = getContactsFromClientsByDebtor(debtorContactList, businessName)
      setContacts(contactsFromClientsBysDebtor)
    }, logError)
  }, [])

  return (
    <>
      <S_NoteEditor className={className} flexType={flexType}>
        <h3 className={`label`}>{label}</h3>
        <div className={'type-document'}>
          <FormField
            className="type-select"
            onChange={setTypeVal}
            options={formattedAvailableTypes}
            placeholder="Please select note type"
            showDefault
            type="select"
            value={typeVal}
          />
          <input multiple={true} onChange={handleFileChange} ref={fileRef} style={{ display: 'none' }} type="file" />
          <Button
            className={'compact'}
            disabled={!typeVal}
            onClick={(e) => fileRef.current.click()}
            text="Attach Files"
          />
        </div>
        <div className="type-document">
          <FormField
            className="type-select"
            disabled={noContacts}
            onChange={setContactVal}
            options={formattedContacts}
            placeholder="Please select a contact"
            showDefault
            type="select"
            value={contactVal}
          />
          <SplitButton
            className="compact"
            customCss={splitButtonCss}
            disabled={!contactVal || noContacts}
            dropdownButtons={[
              {
                label: 'Go to contacts',
                function: () => {
                  history.push(`${LENDER_ROUTES.debtor_details}/${debtorId}/contacts`)
                  closeModal()
                },
              },
              {
                label: 'Create Contact',
                function: () => {
                  handleContactModal({ callFromModal, previousModalProps })
                },
              },
            ]}
            mainButton={{
              label: 'View',
              function: () => handleContactModal({ callFromModal, previousModalProps, readOnly: true }),
            }}
            text="View"
          />
        </div>
        <textarea
          className={'full-width'}
          onChange={(e) => {
            setTextVal(e.target.value)
          }}
          value={textVal}
        />
        {fileNames.map((name) => {
          return <div key={name}>{name}</div>
        })}
        <LoaderButton
          className="save-btn"
          customCss={normalizeButtonWidth}
          isLoading={creatingVerification}
          onClick={handleSave}
          text="Add Note"
        />
      </S_NoteEditor>
    </>
  )
}

VerificationNoteEditor.propTypes = {
  businessName: PropTypes.string,
  callFromModal: PropTypes.bool,
  className: PropTypes.string,
  creatingVerification: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  debtorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flexType: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  previousModalProps: PropTypes.shape({
    Modal: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  }),
  refresh: PropTypes.func,
  save: PropTypes.func,
}

VerificationNoteEditor.defaultProps = {
  callFromModal: false,
  creatingVerification: false,
  previousModalProps: {},
  refresh: () => {},
  save: () => {},
}
